import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
} from '@fluentui/react-components';
import { X } from 'lucide-react';

import { extractConsentError, isGraphError } from '../../api/identity';
import { useIdentityWorkbench } from '../../hooks/useIdentityWorkbench';
import BulkActionResultView from '../BulkActionResultView';
import ConsentMessageBar from './ConsentMessageBar';
import AddMappingPanel from './AddMappingPanel';
import IdentityInspector from './IdentityInspector';
import IdentityQueueSearch from './IdentityQueueSearch';
import IdentityQueueTable from './IdentityQueueTable';
import IdentityQueueToolbar from './IdentityQueueToolbar';
import IdentityStatusTabs from './IdentityStatusTabs';
import { errorMessage } from './identityRowModel';
import { NUMERIC, STROKE } from '../../theme/tokens';

export interface IdentityMappingsSectionProps {
  migrationId: string;
  migrationType: 'T2T' | 'D2C';
  deviceCount: number;
  onAssignDevices?: () => void;
  /**
   * Wave BPRT state. FD-382 (FD-ADR-036, FD-ADR-037 §4) — one predicate, three
   * server behaviours, so the row actions have to be shaped by it rather than
   * discovering it in a 207:
   *   ADD    (no existing mapping) → allowed while active, audited late
   *   UPDATE (existing mapping)    → per-row reject inside `rejected`
   *   REMOVE                       → whole-request 409 MAPPINGS_FROZEN
   * Absent/undefined reads as not frozen, which is the pre-activation state.
   */
  bprtStatus?: string | null;
  /**
   * Tenant identity for the add/change surfaces (FD-381). From the migration
   * record, which already carries all four (FD-110 resolution). The destination
   * may legitimately be absent on D2C — the migration row has no `DestTenantID`
   * when it defaults to the deployment tenant (FD-ADR-029) — in which case the
   * panel names what `tenant-users` reports it actually searched.
   */
  sourceTenantDomain?: string | null;
  sourceTenantId?: string | null;
  destTenantDomain?: string | null;
  destTenantId?: string | null;
}

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
  },
  /**
   * Tabs on the left, the action cluster on the right, ONE rule under both
   * (owner decision, 2026-09-28). The toolbar used to sit up beside the section
   * title, which cost a whole row above a table that wants the height; the
   * underline moves from the TabList to this row so it still runs the full
   * width rather than stopping where the tabs stop.
   */
  controlsRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    columnGap: '16px',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: STROKE.base,
  },
  /**
   * The tab strip is the side that YIELDS, and that is the whole fix (owner,
   * 2026-09-28). FD-515 put both on this row behind `flex-wrap: wrap`, which
   * at the campaign content width put the toolbar straight back onto a second
   * line — six tabs plus the action cluster do not fit in ~1000px, so the row
   * reverted to exactly the two-row layout it was meant to remove.
   *
   * `minWidth: 0` is load-bearing: a flex item's default `min-width: auto`
   * refuses to shrink below its content, so without it the strip pushes the
   * toolbar out of the row no matter what `overflow` says.
   */
  controlsTabs: {
    minWidth: 0,
    overflowX: 'auto',
    scrollbarWidth: 'thin',
  },
  controlsActions: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    paddingBottom: '6px',
  },
  resultsBlock: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '8px',
    alignItems: 'flex-start',
  },
});

/**
 * The identity mapping workspace — mounted twice, on the campaign Identities
 * tab and under `Resources › Identities` (FD-ADR-037 §1).
 *
 * FD-507 reduced this from 1854 lines to a composition. The state, the
 * derivations and the two-call save live in `useIdentityWorkbench`; the queue,
 * its toolbar and its status tabs are their own components; the row model and
 * its helpers are `identityRowModel`. Nothing about what an operator can do
 * changed in the split — the 1675 lines of tests over this behaviour pass
 * unedited, which is the acceptance criterion the split was written against.
 */
export default function IdentityMappingsSection({
  migrationId,
  migrationType,
  deviceCount,
  onAssignDevices,
  bprtStatus,
  sourceTenantDomain,
  sourceTenantId,
  destTenantDomain,
  destTenantId,
}: IdentityMappingsSectionProps) {
  const styles = useStyles();
  const wb = useIdentityWorkbench({ migrationId, migrationType, bprtStatus });
  const {
    query,
    rows,
    totalUniqueUsers,
    devicesWithoutManifest,
    isT2T,
    filter,
    setFilter,
    filterCounts,
    saveResult,
    setSaveResult,
    saveError,
    handleSave,
    bulkNotice,
    setBulkNotice,
    showAddPanel,
    setShowAddPanel,
    unmappedObservedSources,
    handleAddMapping,
    detail,
    setSelectedRowKey,
  } = wb;

  const saveConsent = saveError ? extractConsentError(saveError) : null;

  const sectionCopy = isT2T
    ? 'Map each signed-in source-tenant user to a destination identity before cutover.'
    : 'Review each signed-in user and confirm their Entra identity before cutover.';

  return (
    <section className={styles.section} aria-label="Identity mappings">
      <div>
        <div>
          <h2 className="font-display text-xl tracking-tight text-zinc-900 mb-1">
            Identity mappings
          </h2>
          <p className="font-display text-sm text-zinc-600">{sectionCopy}</p>
          {!query.isLoading && !query.isError && (
            <p className="font-display text-xs text-zinc-500 mt-1">
              <span className="font-mono">{totalUniqueUsers}</span> unique user
              {totalUniqueUsers === 1 ? '' : 's'}
              {devicesWithoutManifest > 0 && (
                <>
                  {' · '}
                  <span className="font-mono">{devicesWithoutManifest}</span> device
                  {devicesWithoutManifest === 1 ? '' : 's'} awaiting manifest
                </>
              )}
              {/* The feed pages, and the tab counts are over the rows LOADED.
                  The retired tile row carried that caveat (FD-508); with the
                  tiles gone it has to be said here, or at 2,847 identities a tab
                  reading "Needs action (50)" is a page count wearing a campaign
                  label. `totalUniqueUsers` above is campaign-wide, from the
                  server. */}
              {rows.length < totalUniqueUsers && (
                <>
                  {' · '}
                  <span style={NUMERIC}>{rows.length.toLocaleString()}</span> loaded — the
                  tab counts below cover the loaded rows
                </>
              )}
            </p>
          )}
        </div>
      </div>

      {saveConsent && <ConsentMessageBar consent={saveConsent} />}
      {saveError != null && !saveConsent && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>
              {isGraphError(saveError)
                ? 'Directory temporarily unavailable'
                : 'Could not save mappings'}
            </MessageBarTitle>
            {isGraphError(saveError)
              ? 'Microsoft Graph did not respond while validating the mappings. Your staged changes are preserved — retry in a moment.'
              : errorMessage(saveError)}
          </MessageBarBody>
          <MessageBarActions>
            <Button size="small" appearance="subtle" onClick={handleSave}>
              Retry save
            </Button>
          </MessageBarActions>
        </MessageBar>
      )}

      {saveResult && (
        <div className={styles.resultsBlock}>
          <BulkActionResultView result={saveResult} keyColumnHeader="User" />
          <Button size="small" appearance="secondary" onClick={() => setSaveResult(null)}>
            Dismiss results
          </Button>
        </div>
      )}

      {/* FD-383 — what the bulk action did NOT do, stated where it was taken.
          A button reading "Approve 8 selected" that stages 5 is worse than one
          that names the 3 it skipped, because nothing else on screen would tell
          the operator which. */}
      {bulkNotice && (
        <MessageBar intent="info">
          <MessageBarBody>{bulkNotice}</MessageBarBody>
          <MessageBarActions
            containerAction={
              <Button
                appearance="transparent"
                aria-label="Dismiss bulk action summary"
                icon={<X size={14} strokeWidth={1.5} />}
                onClick={() => setBulkNotice(null)}
              />
            }
          />
        </MessageBar>
      )}

      {/* Owner decision, 2026-09-28 — the filter tabs and the action cluster on
          ONE row. They were two rows plus a five-tile summary above them, which
          is 200px of chrome over a table an operator scrolls. The tiles are
          retired outright; what was campaign-wide about them survives as the
          count line above and, for progress, as the campaign stepper's
          "Map identities" step, which reads the same facts. */}
      <div className={styles.controlsRow}>
        <div className={styles.controlsTabs}>
          <IdentityStatusTabs filter={filter} counts={filterCounts} onChange={setFilter} />
        </div>
        <div className={styles.controlsActions}>
          <IdentityQueueToolbar wb={wb} />
        </div>
      </div>

      <IdentityQueueSearch wb={wb} />

      {showAddPanel && (
        <AddMappingPanel
          migrationId={migrationId}
          migrationType={migrationType}
          sourceTenantDomain={sourceTenantDomain}
          sourceTenantId={sourceTenantId}
          destTenantDomain={destTenantDomain}
          destTenantId={destTenantId}
          observedSources={unmappedObservedSources}
          onCancel={() => setShowAddPanel(false)}
          onAdd={handleAddMapping}
        />
      )}

      <IdentityQueueTable
        wb={wb}
        deviceCount={deviceCount}
        onAssignDevices={onAssignDevices}
      />


      {/* FD-509 — the inspector, non-modal so the queue behind it stays
          clickable, and MOUNTED ALWAYS with `open` toggled: a Fluent dialog
          surface mounted already-open never enters the accessibility tree
          (FLUENT_UI_ADDENDUM; a drawer is a dialog), and the failure is
          invisible on screen. */}
      <IdentityInspector
        open={detail !== null}
        detail={detail}
        onClose={() => setSelectedRowKey(null)}
        wb={wb}
        migrationId={migrationId}
        migrationType={migrationType}
        destTenantDomain={destTenantDomain}
        destTenantId={destTenantId}
        onViewDevices={onAssignDevices}
      />
    </section>
  );
}
